import React from "react";
import { Calendar, User, Clock, ArrowRight, Share2, Tag } from "lucide-react";
import { PostsProvider, usePosts, type Post } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Card, CardContent, CardFooter, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { cn } from "@/lib/utils";



function PostDate({ date }: { date?: string }) {
  if (!date) return null;
  const dateObj = new Date(date);
  return (
    <div data-section-id="11868" className="flex items-center gap-1.5 text-sm text-muted-foreground">
      <Calendar className="h-4 w-4" />
      <time dateTime={date}>{dateObj.toLocaleDateString("el-GR", { month: "long", day: "numeric", year: "numeric" })}</time>
    </div>
  );
}

function PostAuthor({ author }: { author?: Post["author"] }) {
  if (!author) return null;
  return (
    <div className="flex items-center gap-2 text-sm font-semibold tracking-tight">
      <Avatar className="h-6 w-6">
        <AvatarImage src={author.avatar?.["96"]} alt={author.name} />
        <AvatarFallback>{author.name.charAt(0)}</AvatarFallback>
      </Avatar>
      <span>{author.name}</span>
    </div>
  );
}



function RelatedPostsList({ currentPostId }: { currentPostId: string }) {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-80 animate-pulse rounded-lg bg-muted/50" />
        ))}
      </div>
    );
  }

  if (error || posts.length === 0) return null;

  return (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
      {posts.map((post, i) => (
        <Card key={post.id} className="flex h-full flex-col overflow-hidden border-border bg-card shadow-sm transition-all duration-300 hover:shadow-md" data-index={i}>
          {post.featuredImage && (
            <div className="aspect-video w-full overflow-hidden bg-muted">
              <img
                src={post.featuredImage}
                alt={post.title}
                className="h-full w-full object-cover transition-transform duration-500 hover:scale-105"
              />
            </div>
          )}
          <CardHeader className="p-5 pb-2">
            <div className="mb-2 flex flex-wrap gap-2">
              {post.categories?.slice(0, 1).map((cat) => (
                <span key={cat.id} className="inline-flex items-center rounded-full bg-secondary/10 px-2.5 py-0.5 text-xs font-semibold text-secondary">
                  {cat.name}
                </span>
              ))}
            </div>
            <CardTitle className="line-clamp-2 text-lg font-bold tracking-tight text-foreground">
              <Link to={post.link || "#"} className="hover:text-primary transition-colors">
                {post.title}
              </Link>
            </CardTitle>
          </CardHeader>
          <CardContent className="flex-1 p-5 pt-2">
            <CardDescription className="line-clamp-3 text-muted-foreground/80 leading-relaxed">
              {post.excerpt ? <span dangerouslySetInnerHTML={{ __html: post.excerpt }} /> : "Ενημερωθείτε για τις τελευταίες εξελίξεις στις υπηρεσίες πληροφορικής και ασφάλειας..."}
            </CardDescription>
          </CardContent>
          <CardFooter className="border-t border-border/50 p-5 pt-4">
            <Link to={post.link || "#"} className="w-full">
              <Button variant="ghost" className="w-full justify-between group hover:bg-accent hover:text-accent-foreground">
                Περισσότερα
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Button>
            </Link>
          </CardFooter>
        </Card>
      ))}
    </div>
  );
}

function RelatedPostsSection({ currentPostId }: { currentPostId: string }) {
  
  const relatedQuery = new WP_Query({
    post_type: "post",
    posts_per_page: 3,
    post__not_in: [parseInt(currentPostId)],
    orderby: "date",
    order: "DESC",
    ignore_sticky_posts: true,
  });

  return (
    <div className="mt-16 border-t border-border pt-16">
      <div className="mb-8 flex items-center justify-between">
        <h3 className="text-2xl font-bold tracking-tight text-foreground">Σχετικά Άρθρα & Νέα</h3>
        <Link to="#">
          <Button variant="outline" className="border-primary text-primary hover:bg-primary hover:text-primary-foreground">Δείτε Όλα</Button>
        </Link>
      </div>
      <PostsProvider wp_query={relatedQuery}>
        <RelatedPostsList currentPostId={currentPostId} />
      </PostsProvider>
    </div>
  );
}



function SinglePostContent() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="container mx-auto max-w-4xl px-4 py-12">
        <div className="space-y-4">
          <div className="h-8 w-2/3 animate-pulse rounded-md bg-muted" />
          <div className="h-4 w-1/3 animate-pulse rounded-md bg-muted" />
          <div className="mt-8 h-96 w-full animate-pulse rounded-lg bg-muted" />
          <div className="mt-8 space-y-2">
            <div className="h-4 w-full animate-pulse rounded bg-muted" />
            <div className="h-4 w-full animate-pulse rounded bg-muted" />
            <div className="h-4 w-5/6 animate-pulse rounded bg-muted" />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-20 text-center">
        <h2 className="text-2xl font-bold text-destructive">Σφάλμα Φόρτωσης Άρθρου</h2>
        <p className="mt-2 text-muted-foreground">Δεν μπορέσαμε να ανακτήσουμε το περιεχόμενο. Παρακαλώ δοκιμάστε ξανά αργότερα.</p>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="container mx-auto px-4 py-20 text-center">
        <h2 className="text-2xl font-bold text-foreground">Το Άρθρο δεν Βρέθηκε</h2>
        <p className="mt-2 text-muted-foreground">Το περιεχόμενο που αναζητάτε δεν είναι διαθέσιμο ή έχει μετακινηθεί.</p>
        <Link to="/HomePage" className="mt-6 inline-block">
          <Button className="bg-primary text-primary-foreground hover:opacity-90">Επιστροφή στην Αρχική</Button>
        </Link>
      </div>
    );
  }

  const post = posts[0];

  return (
    <article className="container mx-auto max-w-4xl px-4 py-12 md:py-16">
      {}
      <header className="mb-10 text-center md:mb-14">
        <div className="mb-6 flex flex-wrap justify-center gap-2">
          {post.categories?.map((cat) => (
            <Link key={cat.id} to={cat.link || "#"}>
              <span className="rounded-full bg-primary/10 px-3 py-1 text-sm font-semibold text-primary hover:bg-primary/20 transition-colors">
                {cat.name}
              </span>
            </Link>
          ))}
        </div>
        
        <h1 className="mb-6 text-3xl font-extrabold leading-tight tracking-tighter text-foreground md:text-4xl lg:text-5xl">
          {post.title}
        </h1>

        <div className="flex flex-wrap items-center justify-center gap-6 text-muted-foreground">
          <PostAuthor author={post.author} />
          <span className="hidden h-1 w-1 rounded-full bg-muted-foreground/40 md:block" />
          <PostDate date={post.date} />
          <span className="hidden h-1 w-1 rounded-full bg-muted-foreground/40 md:block" />
          <div className="flex items-center gap-1.5 text-sm font-medium">
            <Clock className="h-4 w-4" />
            <span>5 λεπτά ανάγνωση</span>
          </div>
        </div>
      </header>

      {}
      {post.featuredImage && (
        <div className="mb-12 overflow-hidden rounded-lg shadow-xl md:mb-16">
          <img
            src={post.featuredImage}
            alt={post.title}
            className="h-auto w-full object-cover"
          />
        </div>
      )}

      {}
      <div className="grid grid-cols-1 gap-12 lg:grid-cols-[1fr_280px]">
        <div className="min-w-0">
          <div 
            className="prose prose-lg dark:prose-invert max-w-none 
              prose-headings:font-bold prose-headings:tracking-tighter prose-headings:text-foreground
              prose-p:leading-relaxed prose-p:text-muted-foreground prose-p:mb-6
              prose-a:text-primary prose-a:font-semibold prose-a:no-underline hover:prose-a:underline
              prose-strong:font-bold prose-strong:text-foreground
              prose-ul:list-disc prose-ul:pl-6 prose-ul:mb-6 prose-li:mb-2 prose-li:text-muted-foreground
              prose-img:rounded-lg prose-img:shadow-md
              prose-blockquote:border-l-4 prose-blockquote:border-primary prose-blockquote:pl-6 prose-blockquote:italic prose-blockquote:text-foreground/80"
            dangerouslySetInnerHTML={{ __html: post.content || "" }} 
          />
          
          {}
          {post.tags && post.tags.length > 0 && (
            <div className="mt-10 flex flex-wrap items-center gap-3 border-t border-border pt-8">
              <Tag className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-bold text-muted-foreground uppercase tracking-wider">Ετικέτες:</span>
              {post.tags.map((tag) => (
                <Link key={tag.id} to={tag.link || "#"}>
                  <span className="rounded-md bg-secondary/10 px-2.5 py-1 text-sm font-medium text-secondary hover:bg-secondary/20 transition-colors">
                    #{tag.name}
                  </span>
                </Link>
              ))}
            </div>
          )}
        </div>

        {}
        <aside className="space-y-8">
          <div className="sticky top-24 rounded-lg border border-border bg-card p-6 shadow-md">
            <h3 className="mb-4 text-lg font-bold tracking-tight text-foreground">Σχετικά με τον Συντάκτη</h3>
            <div className="mb-4 flex items-center gap-4">
              <Avatar className="h-16 w-16 border-2 border-primary shadow-sm">
                <AvatarImage src={post.author?.avatar?.["96"]} alt={post.author?.name} />
                <AvatarFallback className="text-lg bg-accent text-accent-foreground">{post.author?.name?.charAt(0)}</AvatarFallback>
              </Avatar>
              <div>
                <p className="font-bold text-foreground leading-tight">{post.author?.name}</p>
                <p className="text-xs font-semibold text-secondary uppercase tracking-tight">Σύμβουλος Τεχνολογίας</p>
              </div>
            </div>
            <p className="mb-6 text-sm leading-relaxed text-muted-foreground">
              Ειδικός σε δίκτυα, συστήματα ασφαλείας και υποδομές IT για μικρές επιχειρήσεις. Στην <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1592747/img/logo.png" alt="Company Logo" className="h-7 inline-block align-middle" /> εξασφαλίζουμε την απρόσκοπτη λειτουργία του γραφείου σας.
            </p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" className="w-full border-primary text-primary hover:bg-primary hover:text-primary-foreground">
                Προφίλ
              </Button>
              <Button variant="ghost" size="icon" className="shrink-0 text-muted-foreground hover:text-primary">
                <Share2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </aside>
      </div>

      {}
      <RelatedPostsSection currentPostId={post.id} />
    </article>
  );
}



export default function SinglePostTemplateSection() {
  
  
  const currentWPQueryParams = typeof window !== 'undefined' && (window as any).wvcClient 
    ? (window as any).wvcClient.getCurrentWPQueryParams() 
    : {};

  
  const wp_query = new WP_Query(currentWPQueryParams);

  return (
    <section id="single-post-template-section" className="bg-background text-foreground w-full">
      <PostsProvider wp_query={wp_query}>
        <SinglePostContent />
      </PostsProvider>
    </section>
  );
}